import type { Branch, BranchCode } from "~/types";
import { COMPANY_NUM } from '../assets/constants';
import { getEnvBranch, isDev } from "./environment";

const companyInfo = {
  idTypeKey: 'company_num',
  number: COMPANY_NUM
} as const;

const branches: Record<BranchCode, Readonly<Branch>> = {
  il: lock({
    id: 'IL',
    code: 'il',
    domain: 'visanet.co.il',
    locale: 'he',
    language: 'he',
    currency: 'ils',
    baseCountry: 'ISR',
    business: companyInfo,
    acknowledgedBranches: ['co', 'il', 'ru', 'se'],
    visaCountries: ['india', 'usa', 'uk', 'vietnam', 'morocco', 'canada', 'sri_lanka', 'israel', 'korea', 'thailand', 'cambodia', 'new_zealand'],
    emailUsername: 'hello',
    trustingCompanies: ['elal', 'nvidia', 'intel', 'lametayel', 'iai', 'parallel_wireless'],
    crispId: '9d20197b-d1c9-441f-b480-dc1ea6a55844',
    blog: 'blog.visanet.co.il',
    phone: {
      raw: '+972587785850',
      display: '058-778-5850'
    },
    whatsappBot: {
      raw: '972556677676',
      display: '055-667-7676'
    },
    socialLinks: {
      google: 'https://goo.gl/maps/eE96aqz7JAtLJAuFA',
      facebook: 'https://he-il.facebook.com/visanet.co.il',
      instagram: 'https://www.instagram.com/visanet.il'
    },
    reviews: {
      public: true,
      links: {
        google: 'https://goo.gl/maps/eE96aqz7JAtLJAuFA',
        facebook: 'https://he-il.facebook.com/visanet.co.il/',
        easy: 'https://easy.co.il/page/10122959'
      }
    },
    companyAddress: {
      line: 'Derech Menachem Begin 144',
      city: 'Tel Aviv',
      country: 'ISR',
      postalCode: '6492124',
      text: 'דרך מנחם בגין 144, תל אביב, 6492124, ישראל'
    },
    passportExample: 'israel.webp',
    officePicture: 'isr.jpg',
    paymentProcessors: ['stripe', 'paypal', 'bit', 'paybox'],
    mobilePayments: {
      processors: ['bit', 'paybox'],
      phone: '054-7616467'
    },
    paymentLogos: ['visa', 'mastercard', 'amex', 'diners', 'paypal', 'bit', 'paybox'],
    productReplacement: {
      korea_3_years: 'korea_2_years'
    }
  }),

  se: lock({
    id: 'SE',
    code: 'se',
    domain: 'visanet.se',
    locale: 'sv',
    language: 'sv',
    currency: 'sek',
    baseCountry: 'SWE',
    acknowledgedBranches: ['co', 'se', 'il'],
    blog: 'se.visanet.blog',
    visaCountries: ['india', 'uk', 'canada', 'usa', 'israel', 'thailand', 'vietnam', 'saudi_arabia', 'korea', 'indonesia', 'bahrain', 'new_zealand', 'cambodia', 'sri_lanka'],
    business: companyInfo,
    trustingCompanies: ['sas', 'spotify', 'klarna', 'swedavia', 'ving_se', 'ikea'],
    emailUsername: 'hello',
    crispId: '37106bbc-fe18-45d0-9d31-a73332cf9a75',
    phone: {
      raw: '+46702467828',
      display: '070-246-7828'
    },
    socialLinks: {
      facebook: 'https://www.facebook.com/visanet.se',
      instagram: 'https://www.instagram.com/visanet.se/',
      x: 'https://x.com/VisanetSE'
    },
    reviews: {
      public: false,
      links: {
        trustpilot: 'https://se.trustpilot.com/review/visanet.co/location/sweden',
        facebook: 'https://www.facebook.com/visanet.se'
      }
    },
    companyAddress: {
      line: 'Malmskillnadsgatan 32, 111 51',
      city: 'Stockholm',
      country: 'SWE',
      postalCode: '111 51',
      text: 'Malmskillnadsgatan 32, 111 51 Stockholm, Sverige'
    },
    passportExample: 'sweden.jpg',
    officePicture: 'swe.webp',
    gtag: 'GTM-TCKSSNWL',
    paymentProcessors: ['stripe', 'paypal'],
    paymentLogos: ['visa', 'mastercard', 'amex', 'diners', 'paypal']
  }),

  co: lock({
    id: 'CO',
    code: 'co',
    domain: 'visanet.co',
    locale: 'en',
    language: 'en',
    currency: 'usd',
    baseCountry: 'USA',
    acknowledgedBranches: ['co', 'il', 'se'],
    visaCountries: ['india', 'usa', 'uk', 'canada', 'israel', 'thailand', 'korea', 'vietnam', 'saudi_arabia', 'indonesia', 'bahrain', 'new_zealand', 'cambodia', 'schengen', 'morocco', 'sri_lanka', 'togo'],
    business: companyInfo,
    trustingCompanies: ['american_airlines', 'intel', 'airbnb', 'revolut', 'pfizer', 'tmobile'],
    emailUsername: 'hi',
    blog: 'co.visanet.blog',
    crispId: '37106bbc-fe18-45d0-9d31-a73332cf9a75',
    phone: {
      raw: '+19179709170',
      display: '+1 917-970-9170'
    },
    socialLinks: {
      instagram: 'https://www.instagram.com/visanet.co',
      facebook: 'https://www.facebook.com/visanet.co'
    },
    reviews: {
      public: false,
      links: {
        trustpilot: 'https://www.trustpilot.com/review/visanet.co',
        facebook: 'https://www.facebook.com/visanet.co',
      }
    },
    companyAddress: {
      country: 'USA',
      city: 'New York',
      line: '430 Park Avenue',
      postalCode: '10022',
      text: '430 Park Avenue, New York, NY 10022, U.S.',
    },
    passportExample: 'canada.jpg',
    officePicture: 'nyc.webp',
    paymentProcessors: ['stripe', 'paypal'],
    paymentLogos: ['visa', 'mastercard', 'amex', 'diners', 'paypal']
  }),

  ru: lock({
    id: 'RU',
    code: 'ru',
    domain: 'visanet.ru',
    locale: 'ru',
    language: 'ru',
    currency: 'rub',
    baseCountry: 'RUS',
    acknowledgedBranches: ['ru'],
    visaCountries: ['india', 'korea', 'israel', 'schengen', 'saudi_arabia', 'thailand', 'vietnam', 'indonesia', 'bahrain', 'cambodia', 'sri_lanka', 'togo'],
    emailUsername: 'privet',
    nonCompany: true,
    crispId: '7b4d4c09-88a0-4a76-a582-6c8dccbb9cf3',
    trustingCompanies: ['tinkoff', 'aeroflot', 'sberbank', 'yandex', 'coral_travel', 's7_airlines'],
    business: {
      idTypeKey: 'tax_id',
      number: '771980103247',
      owner: 'ИП Кривда Иван Викторович'
    },
    phone: {
      raw: '+79771904225',
      display: '+7 977 190-42-25'
    },
    telegram: 'https://t.me/visanetru',
    blog: 'ru.visanet.blog',
    socialLinks: {
      vk: 'https://vk.com/visanetru',
      instagram: 'https://www.instagram.com/visanet.ru/',
      yandex: 'https://yandex.ru/profile/204631911336',
      google: 'https://g.page/r/CbD2oBvlg_5lEBM/',
      facebook: 'https://www.facebook.com/visanet.ru/'
    },
    reviews: {
      public: true,
      links: {
        yandex: 'https://yandex.ru/profile/204631911336',
        google: 'https://g.page/r/CbD2oBvlg_5lEBM/',
        '2gis': 'https://2gis.ru/moscow/firm/70000001082433864'
      }
    },
    companyAddress: {
      line: 'улица Бутырский Вал, 10',
      city: 'Москва',
      country: 'RUS',
      postalCode: '127055',
      text: 'Бутырский Вал 10, Москва, 127055, Россия'
    },
    passportExample: 'russia.jpg',
    officePicture: 'rus.jpg',
    paymentProcessors: ['tbank', 'stripe', 'bill'],
    paymentLogos: ['visa', 'mastercard', 'mir'],
    gtag: 'GTM-5GT6HHQQ',
    yandexMetrica: 95661508,
    fbBanMsg: 'Запрещенная в России организация',
    insurance: {
      countries: ['korea', 'india', 'israel'],
      url: 'https://t-cpa.ru/1pGFd9'
    },
    visaCountryNationalities: {
      sri_lanka: ['RUS']
    },
    productReplacement: {
      saudi_arabia_year: 'saudi_arabia_6_months'
    }
  }),

  kz: lock({
    id: 'KZ',
    code: 'kz',
    domain: 'visanet.kz',
    locale: 'ru-KZ',
    language: 'ru',
    currency: 'kzt',
    baseCountry: 'KAZ',
    acknowledgedBranches: ['kz', 'ru'],
    visaCountries: ['india', 'korea', 'saudi_arabia', 'thailand', 'vietnam', 'indonesia', 'bahrain', 'cambodia', 'schengen', 'sri_lanka', 'togo'],
    emailUsername: 'hi',
    crispId: '7b4d4c09-88a0-4a76-a582-6c8dccbb9cf3',
    trustingCompanies: ['tinkoff', 'aeroflot', 'sberbank', 'yandex', 'coral_travel', 's7_airlines'],
    business: companyInfo,
    phone: {
      raw: '+79771904225',
      display: '+7 977 190-42-25'
    },
    telegram: 'https://t.me/visanetru',
    blog: 'ru.visanet.blog',
    socialLinks: {
      vk: 'https://vk.com/visanetru',
      instagram: 'https://www.instagram.com/visanet.ru/',
      yandex: 'https://yandex.ru/profile/204631911336',
      google: 'https://g.page/r/CbD2oBvlg_5lEBM/',
      facebook: 'https://www.facebook.com/visanet.ru/'
    },
    reviews: {
      public: true,
      links: {
        yandex: 'https://yandex.ru/profile/204631911336',
        google: 'https://g.page/r/CbD2oBvlg_5lEBM/',
        '2gis': 'https://2gis.ru/moscow/firm/70000001082433864'
      }
    },
    companyAddress: {
      line: 'Динмухамед Кунаев, 10 Блок А. 16 этаж. Офис 1602',
      city: 'Астана',
      country: 'KAZ',
      postalCode: '010000',
      text: 'Динмухамед Кунаев, 10 Блок А. 16 этаж. Офис 1602, Астана, 010000, Казахстан'
    },
    passportExample: 'kazakhstan.jpg',
    officePicture: 'kaz.jpg',
    paymentProcessors: ['stripe', 'paypal'],
    paymentLogos: ['visa', 'mastercard', 'mir'],
    gtag: 'GTM-5GT6HHQQ',
    yandexMetrica: 101897468,
    productReplacement: {
      saudi_arabia_year: 'saudi_arabia_6_months',
      korea_3_years: 'korea_3_years_30_stay'
    }
  })
};

export function getBranchCode() {
  return getEnvBranch() as BranchCode;
}

export function getBranch() {
  const code = getBranchCode();
  return getBranchByCode(code);
}

export function getBranchByCode(code: BranchCode) {
  return branches[code];
}

export function isBranchBelongsCompany(branch: Branch) {
  return !branch.nonCompany;
}

function lock(config: Branch): Readonly<Branch> {
  return Object.freeze({
    ...config,
    get domain() {
      if (isDev()) {
        console.warn('Direct access to branch domain', new Error().stack);
      }
      return config.domain;
    }
  });
}

export function getAcknowledgedBranches(branch: Branch, hiddenBranches: BranchCode[] = ['il']) {
  return branch.acknowledgedBranches.filter(b => b !== branch.code && !hiddenBranches.includes(b));
}